import * as pc from "playcanvas";
import { clamp } from "../sim/math";

// TORRENT 3D RTS camera (operator directive 2026-08-02: 100% PlayCanvas).
//
// A tilted perspective orbit camera looking north and down at the battlefield —
// a proper RTS three-quarter view (PITCH_DEG from horizontal), landing between
// the retired 2D oblique feel and a classic top-down RTS. It is the SINGLE
// projection authority: the PlayCanvas renderer drives its real pc.Camera from
// this camera's eye/target/fov, and input (raycast picking, ground orders,
// drag-box) + the DOM HUD (minimap) + the screen-space overlay all project
// through the very same view/projection matrices, so everything stays locked to
// what the GPU actually draws. The deterministic sim never sees any of this.
//
// World mapping: sim ground (x, y) → PlayCanvas world (X = x, Y = height, Z = y).
// Up is +Y; the camera sits south (+Z) of and above its ground target and looks
// north (−Z), so screen-up is north (smaller sim-y), matching the minimap.

export const ICON_ZOOM = 0.82; // below this (px per world unit at centre) → icons

// --- Fixed rig geometry (presentation only) --------------------------------
export const PITCH_DEG = 58; // camera tilt from horizontal (58° ≈ 32° off top-down)
export const FOV_Y = 40; // vertical field of view, degrees
export const NEAR_CLIP = 8;
export const FAR_CLIP = 6000;
const DEG2RAD = Math.PI / 180;
const TARGET_Y = 0; // ground-plane height the camera focuses on / orders land on

// Scratch (module-level, reused — no per-call allocation in hot paths).
const _v4 = new pc.Vec4();
const _v4b = new pc.Vec4();
const _mView = new pc.Mat4();
const _mWorld = new pc.Mat4();
const _mProj = new pc.Mat4();
const _up = new pc.Vec3(0, 1, 0);

export interface ScreenPt { x: number; y: number; behind: boolean }
export interface GroundPt { x: number; y: number }

export class Camera {
  x = 0; // sim-x of the ground target (screen centre look-at)
  y = 0; // sim-y of the ground target
  dist = 620; // eye distance from the target along the tilt axis
  screenW = 800;
  screenH = 600;
  minDist = 240;
  maxDist = 1400;

  private targetDist = 620;
  private zooming = false;
  private userZoomed = false;
  // Zoom-anchor: keep the ground point under the cursor fixed through a dolly.
  private anchorSx = 0;
  private anchorSy = 0;
  private anchorGx = 0;
  private anchorGy = 0;

  private readonly worldW: number;
  private readonly worldH: number;

  // Cached matrices + derived scale, rebuilt lazily whenever the rig moves.
  private dirty = true;
  private viewProj = new pc.Mat4();
  private invViewProj = new pc.Mat4();
  private _zoom = 1; // measured px-per-world-unit at the target (for overlay/FX sizing)
  private eyeX = 0; private eyeY = 0; private eyeZ = 0;

  constructor(worldW: number, worldH: number) {
    this.worldW = worldW;
    this.worldH = worldH;
    this.x = worldW / 2;
    this.y = worldH / 2;
  }

  // --- Rig recomputation -----------------------------------------------------
  private recompute(): void {
    const phi = PITCH_DEG * DEG2RAD;
    const ex = this.x;
    const ey = TARGET_Y + this.dist * Math.sin(phi);
    const ez = this.y + this.dist * Math.cos(phi);
    this.eyeX = ex; this.eyeY = ey; this.eyeZ = ez;
    _mWorld.setLookAt(new pc.Vec3(ex, ey, ez), new pc.Vec3(this.x, TARGET_Y, this.y), _up);
    _mView.copy(_mWorld).invert();
    const aspect = this.screenW / Math.max(1, this.screenH);
    _mProj.setPerspective(FOV_Y, aspect, NEAR_CLIP, FAR_CLIP);
    this.viewProj.mul2(_mProj, _mView);
    this.invViewProj.copy(this.viewProj).invert();
    // Self-calibrate `zoom` = screen px per world unit at the target (measure a
    // 1-unit east step), so overlay/FX sizing tracks the real projection.
    const a = this.projectRaw(this.x, this.y, 0);
    const b = this.projectRaw(this.x + 1, this.y, 0);
    this._zoom = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    this.dirty = false;
  }

  private ensure(): void {
    if (this.dirty) this.recompute();
  }

  /** px-per-world-unit at the screen centre — legacy 2D `zoom` analogue. */
  get zoom(): number {
    this.ensure();
    return this._zoom;
  }

  // --- Projection ------------------------------------------------------------
  private projectRaw(gx: number, gy: number, h: number): ScreenPt {
    _v4.set(gx, h, gy, 1);
    this.viewProj.transformVec4(_v4, _v4b);
    const w = _v4b.w;
    const ndcx = _v4b.x / w;
    const ndcy = _v4b.y / w;
    return {
      x: (ndcx * 0.5 + 0.5) * this.screenW,
      y: (1 - (ndcy * 0.5 + 0.5)) * this.screenH,
      behind: w <= 0,
    };
  }

  /** Project a world point (sim x, y, optional height h) to CSS screen pixels. */
  project(gx: number, gy: number, h = 0): ScreenPt {
    this.ensure();
    return this.projectRaw(gx, gy, h);
  }

  private unproject(ndcx: number, ndcy: number, ndcz: number, out: { x: number; y: number; z: number }): void {
    _v4.set(ndcx, ndcy, ndcz, 1);
    this.invViewProj.transformVec4(_v4, _v4b);
    const w = _v4b.w || 1e-6;
    out.x = _v4b.x / w; out.y = _v4b.y / w; out.z = _v4b.z / w;
  }

  private _pn = { x: 0, y: 0, z: 0 };
  private _pf = { x: 0, y: 0, z: 0 };

  /** Ray from a screen pixel into the world, in PlayCanvas coords (X,Y=up,Z). */
  screenRay(sx: number, sy: number): { ox: number; oy: number; oz: number; dx: number; dy: number; dz: number } {
    this.ensure();
    const ndcx = (sx / this.screenW) * 2 - 1;
    const ndcy = (1 - sy / this.screenH) * 2 - 1;
    this.unproject(ndcx, ndcy, -1, this._pn);
    this.unproject(ndcx, ndcy, 1, this._pf);
    let dx = this._pf.x - this._pn.x;
    let dy = this._pf.y - this._pn.y;
    let dz = this._pf.z - this._pn.z;
    const len = Math.hypot(dx, dy, dz) || 1;
    dx /= len; dy /= len; dz /= len;
    return { ox: this._pn.x, oy: this._pn.y, oz: this._pn.z, dx, dy, dz };
  }

  /** Ground (sim x, y) under a screen pixel — the point an order lands on. */
  groundAt(sx: number, sy: number): GroundPt {
    const r = this.screenRay(sx, sy);
    // Intersect the y = TARGET_Y ground plane.
    let t = Math.abs(r.dy) > 1e-6 ? (TARGET_Y - r.oy) / r.dy : this.dist;
    if (t < 0) t = this.dist; // looking above the horizon: fall back to focus depth
    return {
      x: clamp(r.ox + r.dx * t, -200, this.worldW + 200),
      y: clamp(r.oz + r.dz * t, -200, this.worldH + 200),
    };
  }

  // --- View extent (culling / grid / minimap) --------------------------------
  /** Four ground corners of the view frustum (screen TL, TR, BR, BL). */
  viewCorners(): GroundPt[] {
    return [
      this.groundAt(0, 0),
      this.groundAt(this.screenW, 0),
      this.groundAt(this.screenW, this.screenH),
      this.groundAt(0, this.screenH),
    ];
  }

  /** Axis-aligned ground bounds of the visible region (conservative). */
  viewRect(): { x0: number; y0: number; x1: number; y1: number } {
    const c = this.viewCorners();
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const p of c) {
      if (p.x < x0) x0 = p.x; if (p.x > x1) x1 = p.x;
      if (p.y < y0) y0 = p.y; if (p.y > y1) y1 = p.y;
    }
    return { x0, y0, x1, y1 };
  }

  // --- Rig control for the renderer -----------------------------------------
  getEye(out: pc.Vec3): pc.Vec3 { this.ensure(); return out.set(this.eyeX, this.eyeY, this.eyeZ); }
  getTarget(out: pc.Vec3): pc.Vec3 { return out.set(this.x, TARGET_Y, this.y); }

  // --- Zoom thresholds -------------------------------------------------------
  isIconZoom(): boolean { return this.zoom < ICON_ZOOM; }
  /** 0 at ICON_ZOOM, 1 well below — smooth sprite→icon fade. */
  iconBlend(): number { return clamp((ICON_ZOOM - this.zoom) / (ICON_ZOOM * 0.4), 0, 1); }

  // --- Movement --------------------------------------------------------------
  resize(w: number, h: number): void {
    this.screenW = w;
    this.screenH = h;
    // Whole-map fit gives the far zoom bound (never see much void); a close
    // tactical bound gives the near one. Perspective + tilt make this approximate,
    // and the near-black clear colour reads any corner void as ops-table space.
    const phi = PITCH_DEG * DEG2RAD;
    const tanHalfV = Math.tan((FOV_Y / 2) * DEG2RAD);
    const aspect = w / Math.max(1, h);
    const fitH = (this.worldH * 0.62) * Math.sin(phi) / tanHalfV;
    const fitW = (this.worldW * 0.55) / (tanHalfV * aspect);
    this.maxDist = clamp(Math.max(fitH, fitW), 700, 2400);
    this.minDist = 230;
    if (!this.userZoomed) this.dist = this.targetDist = clamp(this.maxDist * 0.42, this.minDist, this.maxDist);
    else this.dist = this.targetDist = clamp(this.dist, this.minDist, this.maxDist);
    this.dirty = true;
    this.clampToWorld();
  }

  /** Middle-drag pan: keep the ground under the cursor tracking the drag. */
  pan(dxScreen: number, dyScreen: number): void {
    const cx = this.screenW / 2, cy = this.screenH / 2;
    const a = this.groundAt(cx, cy);
    const b = this.groundAt(cx - dxScreen, cy - dyScreen);
    this.x += b.x - a.x;
    this.y += b.y - a.y;
    this.dirty = true;
    this.clampToWorld();
  }

  /** Edge / key pan in world units (already resolved by Input). */
  panWorld(dx: number, dy: number): void {
    this.x += dx;
    this.y += dy;
    this.dirty = true;
    this.clampToWorld();
  }

  /** Request an eased dolly toward a screen anchor (keeps that ground fixed). */
  zoomAt(factor: number, anchorSx: number, anchorSy: number): void {
    this.userZoomed = true;
    // factor > 1 == zoom in == smaller distance.
    this.targetDist = clamp(this.targetDist / factor, this.minDist, this.maxDist);
    this.anchorSx = anchorSx;
    this.anchorSy = anchorSy;
    const g = this.groundAt(anchorSx, anchorSy);
    this.anchorGx = g.x;
    this.anchorGy = g.y;
    this.zooming = true;
  }

  /** Ease the dolly toward its target each render frame. */
  update(dt: number): void {
    if (!this.zooming) return;
    const k = 1 - Math.pow(0.0025, dt);
    this.dist += (this.targetDist - this.dist) * k;
    if (Math.abs(this.targetDist - this.dist) < 0.4) {
      this.dist = this.targetDist;
      this.zooming = false;
    }
    this.dirty = true;
    // Re-anchor: shift the target so the anchored ground stays under the cursor.
    const g = this.groundAt(this.anchorSx, this.anchorSy);
    this.x += this.anchorGx - g.x;
    this.y += this.anchorGy - g.y;
    this.dirty = true;
    this.clampToWorld();
  }

  centerOn(wx: number, wy: number): void {
    this.x = wx;
    this.y = wy;
    this.dirty = true;
    this.clampToWorld();
  }

  private clampToWorld(): void {
    // Keep the focus on (or just off) the map so the void never dominates.
    const m = 40;
    this.x = clamp(this.x, -m, this.worldW + m);
    this.y = clamp(this.y, -m, this.worldH + m);
    this.dirty = true;
  }
}
